import type { FlowDefinition, NavigationRule } from './types';
import { getJsonPointerValue } from './json-pointer';

/**
 * Evaluate navigation rules to find the next screen from the current screen.
 * Rules are evaluated in order; the first matching rule wins.
 * A rule matches when:
 * - from_step_key matches the current flow-local step
 * - if `when` is present, all conditions are satisfied against the state
 */
export function evaluateNavigation(
  rules: NavigationRule[],
  fromStepKey: string,
  state: Record<string, unknown>,
): string | null {
  for (const rule of rules) {
    if (rule.from_step_key !== fromStepKey) continue;
    if (rule.when && !evaluateWhen(rule.when, state)) continue;
    return rule.target_step_key;
  }
  return null;
}

export type NavigationCondition = string | { $in?: string[]; $count?: number; $minCount?: number };

/**
 * Evaluate a `when` condition map against state.
 * Each key is a JSON Pointer state path (e.g. "/screen/week_typical").
 * Values can be a string (exact match) or an operator object whose present
 * operators must ALL hold: `$in` (membership), `$count` (exact cardinality),
 * `$minCount` (at-least cardinality). The map is keyed by path, so a rule that
 * needs both membership and cardinality at one path (e.g. "contains X and is
 * the only selection") expresses them in a single condition object.
 *
 * A multi-select answer arrives as an array of values. In that case a string
 * condition matches when the array *contains* it, and `$in` matches when the
 * array *intersects* the list — so a rule keyed on a single value (e.g. a
 * per-substance branch) can still fire when that value is one of the selections.
 * Scalar state is treated as a one-element array, so scalar behavior is
 * unchanged; for cardinality an absent value (undefined) counts as zero.
 */
/**
 * Exported for reuse outside navigation — e.g. a screen's `required`
 * validation rule can gate on the same `when` shape (a path is required only
 * when some other path's value matches), rather than every rule applying
 * unconditionally.
 */
export function evaluateWhen(
  when: Record<string, NavigationCondition>,
  state: Record<string, unknown>,
): boolean {
  for (const [path, condition] of Object.entries(when)) {
    const stateValue = getJsonPointerValue(state, path);
    const stateValues = Array.isArray(stateValue) ? stateValue : [stateValue];
    if (typeof condition === 'string') {
      if (!stateValues.includes(condition)) return false;
    } else if (condition && typeof condition === 'object') {
      const cardinality = stateValue === undefined ? 0 : stateValues.length;
      if (condition.$in !== undefined) {
        const list = condition.$in;
        if (!stateValues.some((value) => typeof value === 'string' && list.includes(value))) {
          return false;
        }
      }
      if (condition.$count !== undefined && cardinality !== condition.$count) return false;
      if (condition.$minCount !== undefined && cardinality < condition.$minCount) return false;
    }
  }
  return true;
}

/** A known step completes only by an explicit condition or by having no outgoing rules. */
export function isFlowStepTerminal(
  definition: FlowDefinition,
  stepKey: string,
  state: Record<string, unknown>,
): boolean {
  if (!Object.hasOwn(definition.screens, stepKey)) return false;
  const screen = definition.screens[stepKey];
  if (!screen) return false;
  if (screen.completion_when !== undefined) return evaluateWhen(screen.completion_when, state);
  return !definition.navigation.some((rule) => rule.from_step_key === stepKey);
}

/**
 * Resolve the linear path through a flow given the current accumulated state.
 * Walks from initial_step_key, following navigation rules, until no more rules match.
 * Returns the ordered list of flow-local step keys representing the path.
 */
export function resolveLinearPath(
  definition: FlowDefinition,
  state: Record<string, unknown>,
): string[] {
  const path: string[] = [definition.initial_step_key];
  const visited = new Set<string>([definition.initial_step_key]);
  let current = definition.initial_step_key;

  while (true) {
    if (isFlowStepTerminal(definition, current, state)) break;
    const next = evaluateNavigation(definition.navigation, current, state);
    if (!next || visited.has(next)) break;
    path.push(next);
    visited.add(next);
    current = next;
  }

  return path;
}
